import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getDoc, doc, collection, query, where, getDocs } from "firebase/firestore";
import { Eye, Users, MousePointerClick, Timer, BarChart3, MessageSquare, Search, Heart, Phone } from "lucide-react";
import { auth, db, isAdmin } from "../../firebase.js";
import VenueSelect from "../../components/VenueSelect.js";
import { fetchVenueMetrics, fetchEventCount } from "../../openpanel.js";

const RANGES = [
  { value: "7d", label: "Afgelopen 7 dagen" },
  { value: "30d", label: "Afgelopen 30 dagen" },
  { value: "6m", label: "Afgelopen 6 maanden" },
];

function getRangeStart(range) {
  const now = new Date();
  if (range === "7d") return new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
  if (range === "30d") return new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
  if (range === "6m") return new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
}

function formatNumber(n) {
  if (n == null) return "0";
  return new Intl.NumberFormat("nl-NL").format(Math.round(n));
}

function formatDuration(seconds) {
  if (!seconds) return "0s";
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="w-9 h-9 rounded-sm bg-accent-subtle flex items-center justify-center">
          <Icon size={18} strokeWidth={1.5} className="text-accent" />
        </div>
      </div>
      <p className="text-3xl font-heading font-bold text-text-primary mb-1" data-tabular="true">
        {value}
      </p>
      <p className="text-sm text-text-secondary">{label}</p>
    </div>
  );
}

function StatCardSkeleton() {
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="skeleton w-9 h-9 rounded-sm" />
      </div>
      <div className="skeleton h-8 w-20 rounded mb-1" />
      <div className="skeleton h-4 w-28 rounded" />
    </div>
  );
}

const CHART_PADDING = { top: 10, right: 8, bottom: 28, left: 40 };

function BarChart({ series, dataKey }) {
  const [hovered, setHovered] = useState(null);

  if (!series || series.length === 0) return null;

  const values = series.map((d) => d[dataKey] || 0);
  const rawMax = Math.max(...values, 1);
  const step = Math.pow(10, Math.floor(Math.log10(rawMax))) || 1;
  const max = Math.ceil(rawMax / step) * step || 1;

  const chartW = 600;
  const chartH = 180;
  const plotW = chartW - CHART_PADDING.left - CHART_PADDING.right;
  const plotH = chartH - CHART_PADDING.top - CHART_PADDING.bottom;
  const gap = 2;
  const barW = Math.max(2, (plotW - gap * (series.length - 1)) / series.length);

  const yTicks = [];
  const tickCount = Math.min(4, max);
  for (let i = 0; i <= tickCount; i++) {
    const val = Math.round((max / tickCount) * i);
    if (yTicks.length === 0 || val !== yTicks[yTicks.length - 1]) {
      yTicks.push(val);
    }
  }

  const xLabels = [];
  const isMonthly = series.length <= 13 && series.every((d, i, arr) => {
    if (i === 0) return true;
    const prev = new Date(arr[i - 1].date);
    const curr = new Date(d.date);
    return curr.getMonth() !== prev.getMonth();
  });

  if (isMonthly) {
    series.forEach((d, i) => {
      xLabels.push({ i, label: new Date(d.date).toLocaleDateString("nl-NL", { month: "short" }) });
    });
  } else {
    const labelEvery = series.length <= 14 ? 2 : series.length <= 31 ? 5 : 10;
    series.forEach((d, i) => {
      if (i % labelEvery === 0 || i === series.length - 1) {
        xLabels.push({ i, label: new Date(d.date).toLocaleDateString("nl-NL", { day: "numeric", month: "short" }) });
      }
    });
  }

  return (
    <div className="relative">
      {hovered !== null && (
        <div
          className="absolute bg-bg-sidebar text-white text-xs px-2.5 py-1.5 rounded-sm pointer-events-none z-10 whitespace-nowrap"
          style={{
            left: `${((CHART_PADDING.left + hovered * (barW + gap) + barW / 2) / chartW) * 100}%`,
            top: 0,
            transform: "translateX(-50%)",
          }}
        >
          {new Date(series[hovered].date).toLocaleDateString("nl-NL", isMonthly ? { month: "long", year: "numeric" } : { day: "numeric", month: "short" })}
          {" — "}
          {formatNumber(values[hovered])} weergaven
        </div>
      )}
      <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full" style={{ height: 220 }}>
        {yTicks.map((tick) => {
          const y = CHART_PADDING.top + plotH - (tick / max) * plotH;
          return (
            <g key={tick}>
              <line
                x1={CHART_PADDING.left}
                x2={chartW - CHART_PADDING.right}
                y1={y}
                y2={y}
                stroke="currentColor"
                className="text-border"
                strokeWidth={0.5}
              />
              <text
                x={CHART_PADDING.left - 6}
                y={y + 3}
                textAnchor="end"
                className="fill-text-tertiary"
                fontSize={9}
              >
                {tick >= 1000 ? `${(tick / 1000).toFixed(tick % 1000 === 0 ? 0 : 1)}k` : tick}
              </text>
            </g>
          );
        })}

        {values.map((v, i) => {
          const height = (v / max) * plotH;
          const x = CHART_PADDING.left + i * (barW + gap);
          const y = CHART_PADDING.top + plotH - height;
          return (
            <rect
              key={i}
              x={x}
              y={y}
              width={barW}
              height={Math.max(height, 0)}
              rx={1.5}
              className={i === hovered ? "fill-accent" : "fill-accent/60"}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            />
          );
        })}

        {xLabels.map(({ i, label }) => (
          <text
            key={i}
            x={CHART_PADDING.left + i * (barW + gap) + barW / 2}
            y={chartH - 4}
            textAnchor="middle"
            className="fill-text-tertiary"
            fontSize={8}
          >
            {label}
          </text>
        ))}
      </svg>
    </div>
  );
}

function Stats() {
  const [venue, setVenue] = useState(null);
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [aanvragenCount, setAanvragenCount] = useState(null);
  const [impressionCount, setImpressionCount] = useState(null);
  const [favoriteCount, setFavoriteCount] = useState(null);
  const [callClickCount, setCallClickCount] = useState(null);

  const params = useParams();
  const navigate = useNavigate();

  const loadMetrics = useCallback(async (v, r) => {
    if (!v || !v.slug || !v.objectID) return;
    const type = v.type || "venue";
    const isService = type === "service";
    setLoading(true);
    setError("");
    try {
      const [result, convSnap, impressions, favorites, callClicks] = await Promise.all([
        fetchVenueMetrics(v.slug, v.objectID, r, type),
        getDocs(query(collection(db, "conversations"), where("venueId", "==", v.objectID))),
        fetchEventCount(v.objectID, "impression", r, type),
        // Diensten kennen geen favoriet-/belknop; die events bestaan alleen voor venues.
        isService ? Promise.resolve(null) : fetchEventCount(v.objectID, "favorite", r, type),
        isService ? Promise.resolve(null) : fetchEventCount(v.objectID, "call_click", r, type),
      ]);
      setData(result);
      setImpressionCount(impressions);
      setFavoriteCount(favorites);
      setCallClickCount(callClicks);
      const cutoff = getRangeStart(r);
      let count = 0;
      convSnap.forEach((d) => {
        const msg = d.data().messages?.[0];
        if (!msg?.date) return;
        const date = msg.date.toDate ? msg.date.toDate() : new Date(msg.date);
        if (date >= cutoff) count++;
      });
      setAanvragenCount(count);
    } catch (err) {
      console.error("Failed to fetch metrics:", err);
      setError("Er is een fout opgetreden bij het ophalen van de statistieken.");
      setData(null);
      setAanvragenCount(null);
      setImpressionCount(null);
      setFavoriteCount(null);
      setCallClickCount(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (venue?.objectID === params.venueId) return;
    setVenue(null);
    setData(null);
    setAanvragenCount(null);
    setImpressionCount(null);
    setFavoriteCount(null);
    setCallClickCount(null);
    setError("");
    if (!params.venueId) return;
    let cancelled = false;
    (async () => {
      try {
        // Probeer eerst een locatie; valt dan terug op een dienst (zelfde id-ruimte, aparte collecties).
        let snap = await getDoc(doc(db, "locations", params.venueId));
        let type = "venue";
        if (!snap.exists()) {
          snap = await getDoc(doc(db, "services", params.venueId));
          type = "service";
        }
        if (cancelled) return;
        if (!snap.exists()) {
          setError("Locatie of dienst niet gevonden.");
          return;
        }
        const d = snap.data();
        if (d.uid !== auth.currentUser.uid && !isAdmin()) {
          setError("Je hebt geen toegang tot deze pagina.");
          return;
        }
        setVenue({ objectID: snap.id, slug: d.slug, name: d.name || d.bedrijfsnaam, type });
      } catch {
        if (!cancelled) setError("Je hebt geen toegang tot deze pagina.");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.venueId]);

  useEffect(() => {
    if (venue) loadMetrics(venue, range);
  }, [venue, range, loadMetrics]);

  const handleVenueChange = (value, doc) => {
    if (!doc) {
      setVenue(null);
      setData(null);
      setAanvragenCount(null);
      setImpressionCount(null);
      setFavoriteCount(null);
      setCallClickCount(null);
      navigate("/stats");
      return;
    }
    setVenue({ objectID: doc.objectID, slug: doc.slug, name: doc.name || doc.bedrijfsnaam, type: doc.listingType || doc.type || "venue" });
    navigate(`/stats/${value}`);
  };

  const m = data?.metrics;
  const hasData = !!m;
  const isService = venue?.type === "service";
  const stats = [
    { label: "Zoekweergaven", value: hasData ? formatNumber(impressionCount) : "—", icon: Search },
    ...(!isService ? [{ label: "Favorieten", value: hasData ? formatNumber(favoriteCount) : "—", icon: Heart }] : []),
    ...(!isService ? [{ label: "Belacties", value: hasData ? formatNumber(callClickCount) : "—", icon: Phone }] : []),
    { label: "Paginaweergaven", value: hasData ? formatNumber(m?.total_screen_views) : "—", icon: Eye },
    { label: "Unieke bezoekers", value: hasData ? formatNumber(m?.unique_visitors) : "—", icon: Users },
    { label: "Sessies", value: hasData ? formatNumber(m?.total_sessions) : "—", icon: MousePointerClick },
    { label: "Gem. sessieduur", value: hasData ? formatDuration(m?.avg_session_duration) : "—", icon: Timer },
    { label: "Aanvragen", value: hasData ? formatNumber(aanvragenCount) : "—", icon: MessageSquare },
  ];

  const venueId = venue?.objectID || params.venueId;

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex-1 w-full sm:w-auto">
          <VenueSelect
            activeVenueId={venueId}
            activeVenue={venue}
            preloadFirstItem={true}
            includeServices={true}
            onChange={handleVenueChange}
          />
        </div>
        {venueId && (
          <select
            aria-label="Periode"
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="text-sm text-text-secondary bg-bg-sunken border border-border rounded-sm px-3 py-2 cursor-pointer shrink-0"
          >
            {RANGES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        )}
      </div>

      {error && (
        <div className="card p-4 mb-6 border-error bg-error-subtle text-error text-sm">{error}</div>
      )}

      {!venueId && !loading && (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-bg-sunken flex items-center justify-center">
            <BarChart3 size={28} strokeWidth={1.5} className="text-text-tertiary" />
          </div>
          <h3 className="text-lg font-heading font-semibold mb-2">Selecteer een locatie of dienst</h3>
          <p className="text-sm text-text-secondary max-w-sm mx-auto">Kies een locatie of dienst om de statistieken te bekijken.</p>
        </div>
      )}

      {venueId && loading && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <StatCardSkeleton key={i} />
            ))}
          </div>
          <div className="card p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="skeleton h-5 w-28 rounded" />
            </div>
            <div className="skeleton h-48 w-full rounded" />
          </div>
        </>
      )}

      {venueId && !loading && (
        <>
          {!hasData && (
            <div className="card p-4 mb-6 border-border bg-bg-sunken text-text-secondary text-sm flex items-center gap-3">
              <BarChart3 size={18} strokeWidth={1.5} className="text-text-tertiary shrink-0" />
              Er zijn nog geen statistieken beschikbaar voor deze pagina. Zodra uw pagina bezoekers ontvangt, verschijnen hier de gegevens.
            </div>
          )}

          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8 ${!hasData ? "opacity-40 pointer-events-none select-none" : ""}`}>
            {stats.map((stat, i) => (
              <StatCard key={i} {...stat} />
            ))}
          </div>

          <div className={`card p-6 ${!hasData ? "opacity-40 pointer-events-none select-none" : ""}`}>
            <div className="mb-4">
              <h3 className="text-lg font-heading font-bold">Weergaven</h3>
            </div>

            {data?.series?.some((d) => d.total_screen_views > 0) ? (
              <BarChart series={data.series} dataKey="total_screen_views" />
            ) : (
              <div className="flex items-center justify-center h-48 bg-bg-sunken rounded-sm border border-border border-dashed">
                <div className="text-center">
                  <BarChart3 size={32} strokeWidth={1} className="text-text-tertiary mx-auto mb-3" />
                  <p className="text-sm text-text-tertiary">Nog geen weergaven in deze periode</p>
                </div>
              </div>
            )}
          </div>

          <p className="text-xs text-text-tertiary mt-4">Paginaweergaven, bezoekers en sessies worden bijgehouden sinds begin 2026. Tracking van zoekweergaven, favorieten, belacties en aanvraag-events is gestart op 25 maart 2026.</p>
        </>
      )}
    </div>
  );
}

export default Stats;
